import { Link } from "react-router-dom";
import ProgressBar from "../components/ProgressBar";
import { fmtEUR } from "../lib/auth";
import { useSparziele } from "./data";
import type { WidgetComponentProps } from "./registry";
import type { SparzielRow } from "../api/client";

export default function SparzieleWidget({ config, size }: WidgetComponentProps) {
  const rows = useSparziele();
  const count = Number(config.count) || 4;
  if (!rows) return null;
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="section-title">Kleine Sparziele</h2>
        <Link to="/sparziele" className="text-xs text-brand-300 hover:underline">
          Alle →
        </Link>
      </div>
      {rows.length === 0 && <p className="text-sm text-ink-400">Noch keine kleinen Sparziele.</p>}
      <div className={"grid gap-3 " + (size === "full" ? "sm:grid-cols-2" : "")}>
        {rows.slice(0, count).map((r) => (
          <SparzielPreview key={r.id} r={r} />
        ))}
      </div>
    </section>
  );
}

function SparzielPreview({ r }: { r: SparzielRow }) {
  const pct = Math.min(100, Math.max(0, r.progress * 100));
  const reached = Number(r.remaining) <= 0;
  return (
    <article className="relative overflow-hidden rounded-xl border border-ink-800 bg-ink-900 p-4">
      {/* Subtle horizontal fill */}
      <div
        className="absolute inset-y-0 left-0 bg-brand-500/10 transition-all duration-700"
        style={{ width: `${pct}%` }}
      />
      <div className="relative">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-medium text-ink-100">{r.name}</h3>
          {r.past_due ? (
            <span className="badge-bad">Überfällig</span>
          ) : reached ? (
            <span className="badge-good">erreicht</span>
          ) : !r.on_track ? (
            <span className="badge-warn">off-track</span>
          ) : (
            <span className="badge-good">on track</span>
          )}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-xl font-semibold num text-ink-50">
            {fmtEUR(r.attained)}
          </span>
          <span className="text-xs text-ink-500">/ {fmtEUR(r.target_amount)}</span>
          <span className="ml-auto text-xs num text-ink-400">{pct.toFixed(0)} %</span>
        </div>
        <div className="mt-2.5">
          <ProgressBar
            value={Number(r.attained)}
            total={Number(r.target_amount)}
            height={6}
          />
        </div>
        {Number(r.spent) > 0 && (
          <p className="mt-1.5 text-[11px] text-ink-500 num">
            ausgegeben {fmtEUR(r.spent)} · übrig {fmtEUR(r.current_verfuegbar)}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between text-[11px] text-ink-500 num">
          {r.target_date && <span>bis {r.target_date}</span>}
          {r.suggested_monthly && !reached && (
            <span>Vorschlag {fmtEUR(r.suggested_monthly)}/M</span>
          )}
        </div>
      </div>
    </article>
  );
}
